import { Box } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import statuses from "statuses";
import useAPI from "../../../../api/hooks/useAPI";
import UploadFileArea from "../../../../components/UploadFileArea";

const FileFormat = Object.freeze({
  TXT: "TXT",
  HTML: "HTML",
  CSV: "CSV",
  XML: "XML",
});

const FileFormatToExtensions = Object.freeze({
  [FileFormat.TXT]: [".txt"],
  [FileFormat.HTML]: [".html", ".htm"],
  [FileFormat.CSV]: [".csv"],
  [FileFormat.XML]: [".xml"],
});

const MultipartUploadFileArea = ({
  fileKey,
  initialFileName = "",
  fileFormat = FileFormat.TXT,
  fileExtensions = FileFormatToExtensions[fileFormat],
  label,
}) => {
  const queryClient = useQueryClient();
  const {
    startMicrosoftActiveDirectoryFileUpload,
    completeMicrosoftActiveDirectoryFileUpload,
  } = useAPI();
  const [progress, setProgress] = useState([0, 1]);

  const uploadChunk = async ({ url, chunk }) => {
    return axios.put(url, chunk, {
      headers: {
        "Content-Type": "",
      },
    });
  };

  const uploadToS3 = async (file, key, chunkSize, urls, fields) => {
    const etags = [];

    const chunks = splitFileIntoChunks(file, chunkSize);

    for (let i = 0; i < chunks.length; i++) {
      const response = await uploadChunk({
        url: urls[i],
        chunk: chunks[i],
      });
      setProgress([i + 1, chunks.length]);

      if (response && response.headers && response.headers.etag) {
        etags.push(response.headers.etag.replace(/"/g, ""));
      }
    }

    return etags;
  };

  const splitFileIntoChunks = (file, chunkSize) => {
    const chunks = [];
    let start = 0;

    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      chunks.push(file.slice(start, end));
      start = end;
    }

    return chunks;
  };

  const uploadFile = async (file) => {
    const startUploadResponse = await startMicrosoftActiveDirectoryFileUpload({
      target_file: fileKey,
      file_name: file.name,
      file_format: fileFormat,
      content_type: file.type,
      file_size: file.size,
    });

    const {
      urls,
      key,
      fields,
      chunk_size: chunkSize,
      upload_id,
    } = startUploadResponse.data;

    // Step 2: Upload file chunks to S3
    const etags = await uploadToS3(file, key, chunkSize, urls, fields);

    if (etags.length === 0) {
      throw new Error("No chunks were uploaded successfully");
    }

    // Step 3: Complete the upload
    await completeMicrosoftActiveDirectoryFileUpload({
      target_file: fileKey,
      key: key,
      upload_id: upload_id,
      etags: etags.join(","),
    });
  };

  const uploadFileMutation = useMutation({
    mutationFn: uploadFile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["microsoft-active-directory-status"],
      });
    },
  });

  const handleFileSelect = async (file) => {
    if (!file) return;

    setProgress([0, 1]);
    uploadFileMutation.mutate(file);
  };

  let caption = "";
  let captionVariant = "info";
  let disabled = false;

  let selectedFile = null;

  if (uploadFileMutation?.variables?.name) {
    selectedFile = { name: uploadFileMutation.variables.name };
  } else if (initialFileName) {
    selectedFile = { name: initialFileName, size: 0 };
  }

  if (!selectedFile) {
    caption = `Only ${fileFormat} files are allowed`;
  }

  if (uploadFileMutation.isPending) {
    caption = `Uploading... (${Math.floor(
      (progress[0] / progress[1]) * 100
    )}%)`;
    captionVariant = "info";
    disabled = true;
  } else if (uploadFileMutation.isError) {
    captionVariant = "error";

    if (
      uploadFileMutation.error?.response?.status ===
      statuses.code["payload too large"]
    ) {
      caption = `Upload failed. ${uploadFileMutation.error.response.data.detail}`;
    } else if (
      uploadFileMutation.error?.response?.status ===
      statuses.code["bad request"]
    ) {
      caption = `Only ${fileFormat} files are allowed`;
    } else {
      caption = "Upload failed";
    }
  } else if (uploadFileMutation.isSuccess) {
    caption = "Successfully uploaded";
    captionVariant = "success";
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <UploadFileArea
        onFileSelect={handleFileSelect}
        selectedFile={selectedFile}
        caption={caption}
        captionVariant={captionVariant}
        acceptedFileTypes={fileExtensions.join(",")}
        showDeleteIcon={false}
        disabled={disabled}
        label={label}
      />
    </Box>
  );
};

export default MultipartUploadFileArea;
